import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faPlus,
  faEnvelope,
  faCopy,
  faCheck,
  faTimes,
  faCalendar,
  faExclamationTriangle,
  faEye,
  faRedo,
  faFileInvoice,
  faSearch,
  faFilter,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getApiBaseUrl } from '../../services/api';

interface Client {
  id: string;
  client_id: string;
  name: string;
  email: string;
  company_name?: string;
}

interface PaymentRequest {
  id: string;
  client_id: string;
  client_name: string;
  client_email: string;
  requested_by: string;
  requester_name: string;
  amount_cents: number;
  amount: number;
  currency: string;
  description: string;
  invoice_number?: string;
  status: string;
  payment_link_url?: string;
  is_payable: boolean;
  expires_at?: string;
  days_until_expiry?: number;
  paid_at?: string;
  email_sent: boolean;
  created_at: string;
  updated_at: string;
  metadata: any;
}

const PaymentRequests: React.FC = () => {
  const navigate = useNavigate();
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    amount_cents: 0,
    description: '',
    invoice_number: '',
    send_email: true,
    custom_email: '',
    expires_in_days: 7
  });

  useEffect(() => {
    fetchPaymentRequests();
    fetchClients();
  }, [selectedStatus, selectedClient]);

  const fetchPaymentRequests = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (selectedClient !== 'all') {
        params.client_id = selectedClient;
      }
      
      const response = await axios.get(`${getApiBaseUrl()}/billing/payment-requests`, { params });
      setPaymentRequests(response.data.payment_requests);
    } catch (error) {
      console.error('Error fetching payment requests:', error);
      toast.error('Failed to fetch payment requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/super/clients`);
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleCreatePaymentRequest = async () => {
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (formData.amount_cents <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/billing/payment-requests`, formData);
      toast.success('Payment request created successfully');
      setShowModal(false);
      setFormData({
        client_id: '',
        amount_cents: 0,
        description: '',
        invoice_number: '',
        send_email: true,
        custom_email: '',
        expires_in_days: 7
      });
      fetchPaymentRequests();
    } catch (error: any) {
      console.error('Error creating payment request:', error);
      toast.error(error.response?.data?.detail || 'Failed to create payment request');
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Payment link copied to clipboard');
  };

  const handleCancelRequest = async (id: string) => {
    if (!window.confirm('Are you sure you want to cancel this payment request?')) {
      return;
    }

    try {
      await axios.patch(`${getApiBaseUrl()}/billing/payment-requests/${id}/cancel`);
      toast.success('Payment request cancelled');
      fetchPaymentRequests();
    } catch (error) {
      console.error('Error cancelling payment request:', error);
      toast.error('Failed to cancel payment request');
    }
  };

  const handleResendEmail = async (id: string) => {
    try {
      await axios.post(`${getApiBaseUrl()}/billing/payment-requests/${id}/resend`);
      toast.success('Payment request email sent');
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error('Failed to resend email');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'expired': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (cents: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredRequests = paymentRequests.filter(request => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        request.client_name.toLowerCase().includes(query) ||
        request.client_email.toLowerCase().includes(query) ||
        request.description.toLowerCase().includes(query) ||
        (request.invoice_number && request.invoice_number.toLowerCase().includes(query))
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <Button onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          New One-Time Payment Request
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by client, email, description..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                className="px-4 py-2 border rounded-lg"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
              <select
                className="px-4 py-2 border rounded-lg"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <option value="all">All Clients</option>
                {clients.map(client => (
                  <option key={client.client_id} value={client.client_id}>
                    {client.name} {client.company_name && `(${client.company_name})`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Requests</p>
                <p className="text-2xl font-bold">{paymentRequests.length}</p>
              </div>
              <FontAwesomeIcon icon={faFileInvoice} className="text-3xl text-gray-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold">
                  {paymentRequests.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <FontAwesomeIcon icon={faClock} className="text-3xl text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Paid</p>
                <p className="text-2xl font-bold">
                  {paymentRequests.filter(r => r.status === 'paid').length}
                </p>
              </div>
              <FontAwesomeIcon icon={faCheck} className="text-3xl text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(
                    paymentRequests
                      .filter(r => r.status === 'paid')
                      .reduce((sum, r) => sum + r.amount_cents, 0)
                  )}
                </p>
              </div>
              <FontAwesomeIcon icon={faDollarSign} className="text-3xl text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>One-Time Payment Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-4">Client</th>
                  <th className="text-left p-4">Amount</th>
                  <th className="text-left p-4">Description</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Created</th>
                  <th className="text-left p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request) => (
                  <tr key={request.id} className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <div className="font-medium">{request.client_name}</div>
                        <div className="text-sm text-gray-500">{request.client_email}</div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-medium">{formatCurrency(request.amount_cents, request.currency)}</div>
                      {request.invoice_number && (
                        <div className="text-sm text-gray-500">#{request.invoice_number}</div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="max-w-xs truncate">{request.description}</div>
                    </td>
                    <td className="p-4">
                      <Badge className={getStatusColor(request.status)}>
                        {request.status}
                      </Badge>
                      {request.status === 'pending' && request.days_until_expiry !== null && request.days_until_expiry !== undefined && (
                        <div className="text-xs text-gray-500 mt-1">
                          {request.days_until_expiry > 0 
                            ? `Expires in ${request.days_until_expiry} days`
                            : 'Expired'
                          }
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="text-sm">{formatDate(request.created_at)}</div>
                      {request.paid_at && (
                        <div className="text-xs text-gray-500">
                          Paid: {formatDate(request.paid_at)}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {request.is_payable && request.payment_link_url && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCopyLink(request.payment_link_url!)}
                              title="Copy payment link"
                            >
                              <FontAwesomeIcon icon={faCopy} className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResendEmail(request.id)}
                              title="Resend email"
                            >
                              <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleCancelRequest(request.id)}
                            title="Cancel request"
                          >
                            <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredRequests.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No payment requests found
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Payment Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full my-8 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Create One-Time Payment Request</h2>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Client *</label>
                <select
                  className="w-full p-2 border rounded-lg"
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                >
                  <option value="">Select a client</option>
                  {clients.map(client => (
                    <option key={client.client_id} value={client.client_id}>
                      {client.name} {client.company_name && `(${client.company_name})`} - {client.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Amount (USD) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2">$</span>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full pl-8 pr-3 py-2 border rounded-lg"
                    value={formData.amount_cents / 100 || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      amount_cents: Math.round(parseFloat(e.target.value || '0') * 100) 
                    })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <textarea
                  className="w-full p-2 border rounded-lg"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Payment for services rendered..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Invoice Number (Optional)</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-lg"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="INV-2024-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Expires In</label>
                <select
                  className="w-full p-2 border rounded-lg"
                  value={formData.expires_in_days}
                  onChange={(e) => setFormData({ ...formData, expires_in_days: parseInt(e.target.value) })}
                >
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={formData.send_email}
                  onChange={(e) => setFormData({ ...formData, send_email: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="send_email" className="text-sm">
                  Send email notification
                </label>
              </div>

              {formData.send_email && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Custom Email (Optional)
                    <span className="text-xs text-gray-500 ml-1">Leave blank to use client's email</span>
                  </label>
                  <input
                    type="email"
                    className="w-full p-2 border rounded-lg"
                    value={formData.custom_email}
                    onChange={(e) => setFormData({ ...formData, custom_email: e.target.value })}
                    placeholder="custom@email.com"
                  />
                  {formData.custom_email && formData.client_id && (
                    <p className="text-xs text-blue-600 mt-1">
                      Payment link will be sent to: {formData.custom_email}
                    </p>
                  )}
                </div>
              )}
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowModal(false);
                    setFormData({
                      client_id: '',
                      amount_cents: 0,
                      description: '',
                      invoice_number: '',
                      send_email: true,
                      custom_email: '',
                      expires_in_days: 7
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreatePaymentRequest}>
                  Create One-Time Payment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRequests;